import { useEffect } from 'react';
import { Route, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { FlatRoutes } from '@backstage/core-app-api';
import { convertLegacyApp } from '@backstage/core-compat-api';
import { UserIdentity } from '@backstage/core-components';
import { createApp } from '@backstage/frontend-defaults';
import {
  AnalyticsImplementationBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  RouterBlueprint,
  SignInPageBlueprint,
} from '@backstage/plugin-app-react';

import { AppThemes } from '@spotify/confidence-app-theme';
import { wrapCreateBrowserRouterV6 } from '@spotify/confidence-core-react';
import {
  ConfidenceAnalytics,
  Redirect,
  RouterErrorFallback,
  confidenceUserApiRef,
} from '@spotify/confidence-core-react';
import { bindOnboardingRoutes } from '@spotify/confidence-onboarding-react';
import { FeedItemExtensions } from '@spotify/plugin-confidence-activity-react';
import {
  ConfidenceAdminPage,
  adminResourceAliases,
  bindAdminRoutes,
  confidenceAdminPlugin,
} from '@spotify/plugin-confidence-admin';
import { ChirpCodingSessionArtifactExtension } from '@spotify/plugin-confidence-app-module-extensions';
import {
  ErrorDisplay,
  NotFoundErrorPage,
  ResourceAliasRoute,
} from '@spotify/plugin-confidence-app-react';
import {
  AssistantMessageEventExtension,
  ChatErrorEventExtension,
  ConfidenceChatPage,
  CreateAbtestPreviewExtension,
  CreateChartBlockToolExtension,
  CreateChartToolExtension,
  CreateFlagPreviewExtension,
  CreateMetricPreviewExtension,
  CreateSegmentPreviewExtension,
  CreateSubagentToolExtension,
  CreateTableBlockToolExtension,
  DocumentToolExtension,
  DrawToolExtension,
  EscalateToSupportToolExtension,
  NotificationEventExtension,
  SaveToPulseToolExtension,
  ThinkingEventExtension,
  TodoWriteToolExtension,
  ToolConfirmationRequestEventExtension,
  ToolConfirmationResponseEventExtension,
  UpdateAbtestPreviewExtension,
  UpdateFlagPreviewExtension,
  UpdateFlagVariantPreviewExtension,
  UserInterruptEventExtension,
  UserMessageEventExtension,
  bindChatRoutes,
  confidenceChatPlugin,
} from '@spotify/plugin-confidence-chat';
import {
  ChatEventExtensions,
  CodeBlockExtensions,
  ToolExtensions,
} from '@spotify/plugin-confidence-chat-react';
import { ConfidenceCollaborativeDocumentPage } from '@spotify/plugin-confidence-collaboration';
import {
  CollectionEntryExtensions,
  FavoritesPage,
} from '@spotify/plugin-confidence-collections-react';
import { ReviewsHandler } from '@spotify/plugin-confidence-comments-react';
import {
  ConfidenceEventsPage,
  bindEventsRoutes,
} from '@spotify/plugin-confidence-events';
import {
  ConfidenceFlagsPage,
  FlagClientsChangeExtension,
  FlagCollectionEntryExtension,
  FlagOwnerChangeExtension,
  FlagRuleChangeExtension,
  FlagSchemaChangeExtension,
  FlagStringChangeExtension,
  FlagVariantChangeExtension,
  SegmentCreatedExtension,
  SegmentOwnerChangeExtension,
  SegmentStateChangeExtension,
  SegmentStringChangeExtension,
  SegmentTargetingAllocationChangeExtension,
  bindFlagRoutes,
  flagsPlugin,
} from '@spotify/plugin-confidence-flags';
import {
  ConfidenceMaterializedSegmentsPage,
  LocalResolverSdkSupport,
  bindMaterializedSegmentsRoutes,
  confidenceMaterializedSegmentsPlugin,
} from '@spotify/plugin-confidence-flags-module-materialized-segments';
import {
  ConfidenceSegmentsExtension,
  confidenceSegmentsPlugin,
} from '@spotify/plugin-confidence-flags-module-segments';
import { FlagUpdatesChangeExtensions } from '@spotify/plugin-confidence-flags-react';
import { bindHomeModuleChatRoutes } from '@spotify/plugin-confidence-home-module-chat';
import {
  ConfidenceInitiativesPage,
  initiativesPlugin,
} from '@spotify/plugin-confidence-initiatives';
import {
  ConfidenceInsightsPage,
  bindInsightsRoutes,
  pulsesRouteRef as insightsPulsesRouteRef,
  insightsRouteRef,
} from '@spotify/plugin-confidence-insights';
import {
  ConfidenceIssuesPage,
  ConfidenceThemesPage,
  IssueAssigneeChangeExtension,
  IssueAssignmentChangedActivityTypeExtension,
  IssueCreatedActivityTypeExtension,
  IssueDeletedActivityTypeExtension,
  IssueStateChangeExtension,
  IssueStateChangedActivityTypeExtension,
  IssueStringChangeExtension,
  IssueSurfacesChangeExtension,
  IssueUpdatedActivityTypeExtension,
  TaskCreatedActivityTypeExtension,
  TaskDeletedActivityTypeExtension,
  TaskInputRequestedActivityTypeExtension,
  TaskRedirect,
  TaskStateChangeExtension,
  TaskStateChangedActivityTypeExtension,
  TaskStringChangeExtension,
  TaskUpdatedActivityTypeExtension,
  bindIssuesRoutes,
  issuesPlugin,
} from '@spotify/plugin-confidence-issues';
import {
  IssueActivityTypeExtensions,
  IssueUpdatesChangeExtensions,
} from '@spotify/plugin-confidence-issues-react';
import {
  ConfidenceMeasurementsPage,
  ConfidenceMetricsPage,
  MetricCollectionEntryExtension,
  MetricDecimalChangeExtension,
  MetricDurationChangeExtension,
  MetricLabelsChangeExtension,
  MetricMeasurementConfigSuppressExtension,
  MetricOwnerChangeExtension,
  MetricPreferredDirectionChangeExtension,
  MetricStringChangeExtension,
  MetricVarianceReductionConfigChangeExtension,
  bindMetricsRoutes,
  metricsPlugin,
} from '@spotify/plugin-confidence-metrics';
import { MetricUpdatesChangeExtensions } from '@spotify/plugin-confidence-metrics-react';
import { DebugPage } from '@spotify/plugin-confidence-operations';
import {
  CodingSessionArtifactExtension,
  ConfidenceProjectsPage,
  DocumentArtifactExtension,
  ExternalDocumentArtifactExtension,
  GoogleDocArtifactExtension,
  ImageArtifactExtension,
  LinkArtifactExtension,
  ProjectCollectionEntryExtension,
  ResourceArtifactExtension,
  bindProjectsRoutes,
  projectsPlugin,
} from '@spotify/plugin-confidence-projects';
import { ArtifactExtensions } from '@spotify/plugin-confidence-projects-react';
import {
  PulseArtifactExtension,
  bindPulseRoutes,
} from '@spotify/plugin-confidence-pulse';
import {
  ConfidenceRecordingsPage,
  RecordingToolExtension,
  RecordingsCodeBlockExtension,
  bindRecordingsRoutes,
  recordingsPlugin,
} from '@spotify/plugin-confidence-recordings';
import {
  ConfidenceSurfacesPage,
  SurfaceCollectionEntryExtension,
  surfacesPlugin,
} from '@spotify/plugin-confidence-surfaces';
import {
  ConfidenceUserSettingsPage,
  confidenceUserSettingsPlugin,
} from '@spotify/plugin-confidence-user-settings';
import {
  AudienceFlagsChangeExtension,
  AudienceSegmentChangeExtension,
  ConfidenceWorkflowsPage,
  FlagsModuleSuppressExtension,
  InitiativeChangeExtension,
  LabelsChangeExtension,
  MetricDeliveryFeedItemExtension,
  PlanningChangeExtension,
  RecommendationChangedFeedItemExtension,
  SampleRatioMismatchFeedItemExtension,
  SampleSizeChangeExtension,
  StatsChangeExtension,
  SurfacesChangeExtension,
  TrafficValidationFeedItemExtension,
  WorkflowInstanceCollectionEntryExtension,
  WorkflowInstanceFeedItemExtension,
  WorkflowOwnerChangeExtension,
  WorkflowStateChangeExtension,
  WorkflowStringChangeExtension,
  bindWorkflowsRoutes,
  workflowsPlugin,
} from '@spotify/plugin-confidence-workflows';
import {
  ABTestWorkflowExtension,
  DecisionChangeExtension,
  HypothesisChangeExtension,
  MetricsChangeExtension,
  ReportChangeExtension,
  ReviewsChangeExtension,
  SegmentChangeExtension,
  TreatmentsChangeExtension,
  WorkflowEditorDataExtension,
} from '@spotify/plugin-confidence-workflows-module-ab-test';
import { WorkflowInstanceRuleChangeExtension } from '@spotify/plugin-confidence-workflows-module-flags';
import {
  ExposureKeyChangeExtension,
  ReanalysisConfigChangeExtension,
  ReanalysisConfigSuppressExtension,
  HypothesisChangeExtension as ReanalysisHypothesisChangeExtension,
  ReanalysisTreatmentsChangeExtension,
  ReanalysisWorkflowExtension,
} from '@spotify/plugin-confidence-workflows-module-reanalysis';
import { ReportEditorDataExtensions } from '@spotify/plugin-confidence-workflows-module-report';
import {
  RolloutDescriptionChangeExtension,
  RolloutFlagChangeExtension,
  FlagVariantChangeExtension as RolloutFlagVariantChangeExtension,
  ReachChangeExtension as RolloutReachChangeExtension,
  ScheduleChangeExtension as RolloutScheduleChangeExtension,
  RolloutWorkflowExtension,
} from '@spotify/plugin-confidence-workflows-module-rollout';
import {
  WorkflowExtensions,
  WorkflowExtensionsInitializer,
  WorkflowUpdatesChangeExtensions,
} from '@spotify/plugin-confidence-workflows-react';

import { AppApis } from './apis';
import { confidenceApi } from './apis/Apis';
import CommentsHandler from './components/CommentsHandler';
import { ConnectPage } from './components/ConnectPage';
import { EmailVerifiedRoute } from './components/EmailVerified';
import { HomePage } from './components/HomePage';
import { InvitationPage } from './components/InvitationPage';
import { LoginPage } from './components/LoginPage';
import OverrideHandler from './components/OverrideHandler';
import RedirectHandler from './components/RedirectHandler';
import { RootWrapper } from './components/RootWrapper';
import { Layout } from './components/Sidebar';
import { SignUpPage, UnsupportedMarketRoute } from './components/SignUp';
import { SlackPage } from './components/SlackPage';

const LegacySegmentRedirect = () => {
  const { id } = useParams();
  return <Redirect to={`/segments/${id}`} />;
};

const workflowExtensions = (
  <WorkflowExtensions>
    <ABTestWorkflowExtension />
    <RolloutWorkflowExtension />
    <ReanalysisWorkflowExtension />
  </WorkflowExtensions>
);

const flagChangeExtensions = (
  <FlagUpdatesChangeExtensions>
    <WorkflowInstanceRuleChangeExtension />
    <FlagClientsChangeExtension />
    <FlagOwnerChangeExtension />
    <FlagSchemaChangeExtension />
    <FlagVariantChangeExtension />
    <FlagRuleChangeExtension />
    <FlagStringChangeExtension />
  </FlagUpdatesChangeExtensions>
);

const segmentChangeExtensions = (
  <FlagUpdatesChangeExtensions>
    <SegmentOwnerChangeExtension />
    <SegmentTargetingAllocationChangeExtension />
    <SegmentStringChangeExtension />
    <SegmentCreatedExtension />
    <SegmentStateChangeExtension />
  </FlagUpdatesChangeExtensions>
);

const metricChangeExtensions = (
  <MetricUpdatesChangeExtensions>
    <MetricOwnerChangeExtension />
    <MetricLabelsChangeExtension />
    <MetricDurationChangeExtension />
    <MetricDecimalChangeExtension />
    <MetricPreferredDirectionChangeExtension />
    <MetricVarianceReductionConfigChangeExtension />
    <MetricMeasurementConfigSuppressExtension />
    <MetricStringChangeExtension />
  </MetricUpdatesChangeExtensions>
);

const workflowChangeExtensions = (
  <WorkflowUpdatesChangeExtensions>
    <TreatmentsChangeExtension />
    <AudienceFlagsChangeExtension />
    <FlagsModuleSuppressExtension />
    <AudienceSegmentChangeExtension />
    <InitiativeChangeExtension />
    <LabelsChangeExtension />
    <PlanningChangeExtension />
    <StatsChangeExtension />
    <HypothesisChangeExtension />
    <MetricsChangeExtension />
    <DecisionChangeExtension />
    <ReportChangeExtension />
    <ReviewsChangeExtension />
    <SegmentChangeExtension />
    <WorkflowStateChangeExtension />
    <WorkflowOwnerChangeExtension />
    <SurfacesChangeExtension />
    <WorkflowStringChangeExtension />
    <SampleSizeChangeExtension />
    <RolloutDescriptionChangeExtension />
    <RolloutFlagChangeExtension />
    <RolloutFlagVariantChangeExtension />
    <RolloutReachChangeExtension />
    <RolloutScheduleChangeExtension />
    <ExposureKeyChangeExtension />
    <ReanalysisHypothesisChangeExtension />
    <ReanalysisTreatmentsChangeExtension />
    <ReanalysisConfigSuppressExtension />
    <ReanalysisConfigChangeExtension />
  </WorkflowUpdatesChangeExtensions>
);

const editorDataExtensions = (
  <ReportEditorDataExtensions>
    <WorkflowEditorDataExtension />
  </ReportEditorDataExtensions>
);

const issueChangeExtensions = (
  <IssueUpdatesChangeExtensions>
    <IssueStateChangeExtension />
    <IssueAssigneeChangeExtension />
    <IssueSurfacesChangeExtension />
    <IssueStringChangeExtension />
    <TaskStateChangeExtension />
    <TaskStringChangeExtension />
  </IssueUpdatesChangeExtensions>
);

const issueActivityTypeExtensions = (
  <IssueActivityTypeExtensions>
    <IssueCreatedActivityTypeExtension />
    <IssueStateChangedActivityTypeExtension />
    <IssueAssignmentChangedActivityTypeExtension />
    <IssueUpdatedActivityTypeExtension />
    <IssueDeletedActivityTypeExtension />
    <TaskCreatedActivityTypeExtension />
    <TaskStateChangedActivityTypeExtension />
    <TaskInputRequestedActivityTypeExtension />
    <TaskUpdatedActivityTypeExtension />
    <TaskDeletedActivityTypeExtension />
  </IssueActivityTypeExtensions>
);

const activityFeedExtensions = (
  <FeedItemExtensions>
    <WorkflowInstanceFeedItemExtension />
    <RecommendationChangedFeedItemExtension />
    <TrafficValidationFeedItemExtension />
    <MetricDeliveryFeedItemExtension />
    <SampleRatioMismatchFeedItemExtension />
  </FeedItemExtensions>
);

const artifactExtensions = (
  <ArtifactExtensions>
    <DocumentArtifactExtension />
    <ExternalDocumentArtifactExtension />
    <ChirpCodingSessionArtifactExtension />
    <CodingSessionArtifactExtension />
    <PulseArtifactExtension />
    <ResourceArtifactExtension />
    <GoogleDocArtifactExtension />
    <ImageArtifactExtension />
    <LinkArtifactExtension />
  </ArtifactExtensions>
);

const toolExtensions = (
  <ToolExtensions>
    <CreateChartToolExtension />
    <CreateChartBlockToolExtension />
    <CreateTableBlockToolExtension />
    <CreateSubagentToolExtension />
    <DocumentToolExtension />
    <DrawToolExtension />
    <SaveToPulseToolExtension />
    <TodoWriteToolExtension />
    <EscalateToSupportToolExtension />
    <RecordingToolExtension />
    <CreateFlagPreviewExtension />
    <UpdateAbtestPreviewExtension />
    <UpdateFlagPreviewExtension />
    <UpdateFlagVariantPreviewExtension />
    <CreateAbtestPreviewExtension />
    <CreateMetricPreviewExtension />
    <CreateSegmentPreviewExtension />
  </ToolExtensions>
);

const codeBlockExtensions = (
  <CodeBlockExtensions>
    <RecordingsCodeBlockExtension />
  </CodeBlockExtensions>
);

const chatEventExtensions = (
  <ChatEventExtensions>
    <UserMessageEventExtension />
    <AssistantMessageEventExtension />
    <ThinkingEventExtension />
    <ToolConfirmationRequestEventExtension />
    <ToolConfirmationResponseEventExtension />
    <NotificationEventExtension />
    <UserInterruptEventExtension />
    <ChatErrorEventExtension />
  </ChatEventExtensions>
);

const collectionEntryExtensions = (
  <CollectionEntryExtensions>
    <MetricCollectionEntryExtension />
    <FlagCollectionEntryExtension />
    <SurfaceCollectionEntryExtension />
    <WorkflowInstanceCollectionEntryExtension />
    <ProjectCollectionEntryExtension />
  </CollectionEntryExtensions>
);

const routes = (
  <FlatRoutes>
    {/* ========== PUBLIC ROUTES ========== */}
    {/* These routes render without authentication (see RootWrapper.tsx PUBLIC_ROUTES) */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/invitation" element={<InvitationPage />} />
    <Route
      path="/signup/unsupported-market"
      element={<UnsupportedMarketRoute />}
    />
    <Route path="/signup" element={<SignUpPage />} />
    <Route path="/signup/:id" element={<SignUpPage />} />
    <Route path="/beta/:id" element={<SignUpPage />} />
    <Route path="/email-verified" element={<EmailVerifiedRoute />} />
    <Route path="/override" element={<OverrideHandler />} />

    {/* ========== PROTECTED ROUTES ========== */}
    {/* All routes below require authentication (enforced by RootWrapper) */}
    <Route
      path="/"
      element={
        <HomePage>
          {activityFeedExtensions}
          <WorkflowExtensionsInitializer>
            {workflowExtensions}
          </WorkflowExtensionsInitializer>
        </HomePage>
      }
    />
    <Route path="/slack" element={<SlackPage />} />
    <Route path="/connect" element={<ConnectPage />} />
    <Route path="/redirect" element={<RedirectHandler />} />
    <Route path="/comments" element={<CommentsHandler />} />
    <Route path="/reviews" element={<ReviewsHandler />} />
    <Route
      path="/collaborativeDocuments/*"
      element={
        <ConfidenceCollaborativeDocumentPage>
          {codeBlockExtensions}
        </ConfidenceCollaborativeDocumentPage>
      }
    />
    <Route
      path="/workflows"
      element={
        <ConfidenceWorkflowsPage>
          {workflowExtensions}
          {workflowChangeExtensions}
          {editorDataExtensions}
        </ConfidenceWorkflowsPage>
      }
    />
    <Route
      path="/flags"
      element={
        <ConfidenceFlagsPage>
          <WorkflowExtensionsInitializer>
            {workflowExtensions}
          </WorkflowExtensionsInitializer>
          {flagChangeExtensions}
        </ConfidenceFlagsPage>
      }
    />
    <Route path="/flags/segments/:id" element={<LegacySegmentRedirect />} />
    <Route
      path="/segments"
      element={
        <ConfidenceSegmentsExtension>
          {flagChangeExtensions}
          {segmentChangeExtensions}
        </ConfidenceSegmentsExtension>
      }
    />
    <Route
      path="/materializedSegments"
      element={
        <ConfidenceMaterializedSegmentsPage
          LocalResolverSection={LocalResolverSdkSupport}
        />
      }
    />
    <Route
      path="/metrics"
      element={
        <ConfidenceMetricsPage>{metricChangeExtensions}</ConfidenceMetricsPage>
      }
    />
    <Route
      path="/measurements"
      element={
        <ConfidenceMeasurementsPage>
          {metricChangeExtensions}
        </ConfidenceMeasurementsPage>
      }
    />
    <Route
      path="/favorites"
      element={
        <FavoritesPage>
          <WorkflowExtensionsInitializer>
            {workflowExtensions}
          </WorkflowExtensionsInitializer>
          {collectionEntryExtensions}
        </FavoritesPage>
      }
    />
    <Route path="/events" element={<ConfidenceEventsPage />} />
    <Route
      path="/surfaces"
      element={
        <ConfidenceSurfacesPage>{workflowExtensions}</ConfidenceSurfacesPage>
      }
    />
    <Route
      path="/initiatives"
      element={
        <ConfidenceInitiativesPage>
          {workflowExtensions}
        </ConfidenceInitiativesPage>
      }
    />
    <Route
      path="/projects/*"
      element={
        <ConfidenceProjectsPage>
          {artifactExtensions}
          {chatEventExtensions}
          {toolExtensions}
          {codeBlockExtensions}
        </ConfidenceProjectsPage>
      }
    />
    <Route path="/tasks/:id" element={<TaskRedirect />} />
    <Route
      path="/chats/*"
      element={
        <ConfidenceChatPage>
          {artifactExtensions}
          {chatEventExtensions}
          {toolExtensions}
          {codeBlockExtensions}
        </ConfidenceChatPage>
      }
    />
    <Route
      path="/admin"
      element={
        <ConfidenceAdminPage>{collectionEntryExtensions}</ConfidenceAdminPage>
      }
    />
    <Route path="/debug" element={<DebugPage />} />
    <Route path="/insights" element={<ConfidenceInsightsPage />} />
    <Route
      path="/issues/*"
      element={
        <ConfidenceIssuesPage>
          {chatEventExtensions}
          {toolExtensions}
          {codeBlockExtensions}
          {issueChangeExtensions}
          {issueActivityTypeExtensions}
        </ConfidenceIssuesPage>
      }
    />
    <Route path="/themes" element={<ConfidenceThemesPage />} />
    <Route path="/recordings" element={<ConfidenceRecordingsPage />} />
    <Route path="/settings/*" element={<ConfidenceUserSettingsPage />} />
    <Route
      path="/:resource"
      element={<ResourceAliasRoute aliases={adminResourceAliases} />}
    />
  </FlatRoutes>
);

const sentryCreateBrowserRouter =
  wrapCreateBrowserRouterV6(createBrowserRouter);

const Router = RouterBlueprint.make({
  params: {
    component: ({ children }) => {
      const router = sentryCreateBrowserRouter([
        {
          path: '*',
          Component: () => <>{children}</>,
          errorElement: <RouterErrorFallback />,
        },
      ]);

      return <RouterProvider router={router} />;
    },
  },
});

// Confidence auth setup should be re-done with Backstage identity
// to make this all work properly (just workaround for now)
const SignInPage = SignInPageBlueprint.make({
  params: {
    loader: () =>
      Promise.resolve(({ onSignInSuccess, children }) => {
        useEffect(() => {
          onSignInSuccess(UserIdentity.createGuest());
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return children;
      }),
  },
});

const confidenceAnalyticsImplementation = AnalyticsImplementationBlueprint.make(
  {
    params: defineParams =>
      defineParams({
        deps: { confidenceUserApi: confidenceUserApiRef },
        factory: ({ confidenceUserApi }) =>
          new ConfidenceAnalytics({ confidenceApi, confidenceUserApi }),
      }),
  },
);

export const AppModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPage,
    Router,
    RootWrapper,
    Layout,
    NotFoundErrorPage,
    ErrorDisplay,
    ...AppThemes,
    ...AppApis,
    confidenceAnalyticsImplementation,
  ],
});

const app = createApp({
  features: [...convertLegacyApp(routes), AppModule],
  bindRoutes({ bind }) {
    bindWorkflowsRoutes(bind, {
      flag: flagsPlugin.routes.flag,
      flags: flagsPlugin.routes.flags,
      metric: metricsPlugin.routes.metric,
      metrics: metricsPlugin.routes.metrics,
      entity: confidenceAdminPlugin.routes.entity,
      entities: confidenceAdminPlugin.routes.entities,
      assignmentTable: confidenceAdminPlugin.routes.assignmentTable,
      assignmentTablesCreate:
        confidenceAdminPlugin.routes.assignmentTablesCreate,
      factTable: confidenceAdminPlugin.routes.factTable,
      surface: surfacesPlugin.routes.surface,
      surfaceAvailability: surfacesPlugin.routes.surfaceAvailability,
      labels: confidenceAdminPlugin.routes.labels,
    });

    bindMetricsRoutes(bind, {
      workflowInstance: workflowsPlugin.routes.workflowInstance,
      entities: confidenceAdminPlugin.routes.entities,
      entity: confidenceAdminPlugin.routes.entity,
      assignmentTable: confidenceAdminPlugin.routes.assignmentTable,
      assignmentTablesCreate:
        confidenceAdminPlugin.routes.assignmentTablesCreate,
      factTable: confidenceAdminPlugin.routes.factTable,
      factTables: confidenceAdminPlugin.routes.factTables,
      factTablesCreate: confidenceAdminPlugin.routes.factTablesCreate,
      metricsDataWarehouse: confidenceAdminPlugin.routes.metricsDataWarehouse,
      labels: confidenceAdminPlugin.routes.labels,
      collections: confidenceAdminPlugin.routes.collections,
      collection: confidenceAdminPlugin.routes.collection,
    });

    bindFlagRoutes(bind, {
      workflow: workflowsPlugin.routes.workflow,
      workflowInstance: workflowsPlugin.routes.workflowInstance,
      clients: confidenceAdminPlugin.routes.clients,
      clientSchema: confidenceAdminPlugin.routes.clientSchema,
      client: confidenceAdminPlugin.routes.client,
      labels: confidenceAdminPlugin.routes.labels,
      github: confidenceAdminPlugin.routes.github,

      segments: confidenceSegmentsPlugin.routes.segments,
      segment: confidenceSegmentsPlugin.routes.segment,
      materializedSegments:
        confidenceMaterializedSegmentsPlugin.routes.materializedSegments,
      recordingsExplorer: recordingsPlugin.routes['recordings-explorer'],
    });

    bindMaterializedSegmentsRoutes(bind, {
      segments: confidenceSegmentsPlugin.routes.segments,
    });

    bindEventsRoutes(bind, {
      entity: confidenceAdminPlugin.routes.entity,
      entities: confidenceAdminPlugin.routes.entities,
    });

    bindInsightsRoutes(bind, {
      surface: surfacesPlugin.routes.surface,
    });

    bindIssuesRoutes(bind, {
      chat: confidenceChatPlugin.routes.chat,
    });

    bindProjectsRoutes(bind, {
      agentContext: confidenceAdminPlugin.routes.agentContext,
      chat: confidenceChatPlugin.routes.chat,
      initiative: initiativesPlugin.routes.initiative,
      pulses: insightsPulsesRouteRef,
      surface: surfacesPlugin.routes.surface,
    });

    bindHomeModuleChatRoutes(bind, {
      chat: confidenceChatPlugin.routes.chat,
      projectChat: projectsPlugin.routes.projectChat,
    });

    bindChatRoutes(bind, {
      agentContext: confidenceAdminPlugin.routes.agentContext,
      projectChat: projectsPlugin.routes.projectChat,
      pulses: insightsPulsesRouteRef,
    });

    bindPulseRoutes(bind, {
      chat: confidenceChatPlugin.routes.chat,
      pulsesPage: insightsPulsesRouteRef,
    });

    bindRecordingsRoutes(bind, {
      agentContext: confidenceAdminPlugin.routes.agentContext,
      issue: issuesPlugin.routes.issue,
    });

    bindAdminRoutes(bind, {
      metrics: metricsPlugin.routes.metrics,
      userSettings: confidenceUserSettingsPlugin.routes['user-settings'],
    });

    bindOnboardingRoutes(bind, {
      adminClients: confidenceAdminPlugin.routes.clients,
      flags: flagsPlugin.routes.flags,
      metrics: metricsPlugin.routes.metrics,
      workflow: workflowsPlugin.routes.workflow,
      metricsDataWarehouse: confidenceAdminPlugin.routes.metricsDataWarehouse,
      flagAppliedConnections:
        confidenceAdminPlugin.routes.flagAppliedConnections,
      assignmentTables: confidenceAdminPlugin.routes.assignmentTables,
      factTables: confidenceAdminPlugin.routes.factTables,
      chats: confidenceChatPlugin.routes.chats,
      insights: insightsRouteRef,
    });
  },
});

export default app.createRoot();
